import { RouterPaths } from 'src/components/Router/Router.constants';
import { eUserRole } from 'src/providers/UserProvider/User.enums';
import type {
  tAlertCopy,
  tAlertCta,
  tAlertVariant,
} from './AlertCenter.types';
import { CLOSABLE_WAPI_CASES } from './WapiDisconnection.functions';
import type {
  tWapiDisconnection,
  tWapiDisconnectionCase,
} from './WapiDisconnection.types';

/** Destinations Producto fixed for the notices (spec of 2026-10-19). */
export const WAPI_DISCONNECTION_LINKS = {
  VIDEO_COEXISTENCIA: 'https://www.youtube.com/watch?v=sfyVsn6Avyo',
  VIDEO_PERSONAL_A_BUSINESS: 'https://www.youtube.com/watch?v=xWeE7c0Fryk&t=47s',
  ARTICULO_CLOUD_API: 'https://whatsapp-cloud-link.base44.app/',
  AGENDAR_SESION: 'https://leadsales-ops-mkt.leadsalesapi.com/',
  CREAR_PLANTILLA: `${RouterPaths.wapi}?window=create_template`,
} as const;

/** The `cta_kind` values the disconnection notices report. */
export type tWapiDisconnectionCtaKind =
  | 'reconnect'
  | 'schedule_session'
  | 'cloud_api_article'
  | 'video'
  | 'support'
  | 'create_template'
  | 'dismiss';

const NS = 'wapiDisconnection';

const RECONNECT_ACTION = {
  kind: 'action',
  action: 'connect-wapi',
  analyticsKind: 'reconnect',
} as const;

const CTA = {
  reconnect: { labelKey: `${NS}.cta.reconnect`, ...RECONNECT_ACTION },
  connectNewNumber: {
    labelKey: `${NS}.cta.connectNewNumber`,
    ...RECONNECT_ACTION,
  },
  connect: { labelKey: `${NS}.cta.connect`, ...RECONNECT_ACTION },
  stepByStep: {
    labelKey: `${NS}.cta.stepByStep`,
    kind: 'external',
    target: WAPI_DISCONNECTION_LINKS.VIDEO_COEXISTENCIA,
    analyticsKind: 'video',
  },
  howToChange: {
    labelKey: `${NS}.cta.howToChange`,
    kind: 'external',
    target: WAPI_DISCONNECTION_LINKS.VIDEO_PERSONAL_A_BUSINESS,
    analyticsKind: 'video',
  },
  scheduleSession: {
    labelKey: `${NS}.cta.scheduleSession`,
    kind: 'external',
    target: WAPI_DISCONNECTION_LINKS.AGENDAR_SESION,
    analyticsKind: 'schedule_session',
  },
  cloudApi: {
    labelKey: `${NS}.cta.cloudApi`,
    kind: 'external',
    target: WAPI_DISCONNECTION_LINKS.ARTICULO_CLOUD_API,
    analyticsKind: 'cloud_api_article',
  },
  support: {
    labelKey: `${NS}.cta.support`,
    kind: 'intercom',
    messageKey: `${NS}.supportMessage`,
    analyticsKind: 'support',
  },
  createTemplate: {
    labelKey: `${NS}.cta.createTemplate`,
    kind: 'navigate',
    target: WAPI_DISCONNECTION_LINKS.CREAR_PLANTILLA,
    analyticsKind: 'create_template',
  },
} satisfies Record<string, NonNullable<tAlertCta>>;

type tCaseSpec = {
  /** The copy key segment: cases 4 and 5 share one notice. */
  key: string;
  variant: tAlertVariant;
  hasBody: boolean;
  primary: tAlertCta;
  secondary?: tAlertCta;
  inlineSupport?: boolean;
  /** The seller's note, or null for none. */
  sellerNote: 'reconnect' | 'support' | 'reconnecting' | 'otherAccount' | null;
  /** The event and code support reads in the pre-written message. */
  event: string;
};

const CASES: Record<tWapiDisconnectionCase, tCaseSpec> = {
  '0': {
    key: 'case0',
    variant: 'error',
    hasBody: true,
    primary: CTA.reconnect,
    inlineSupport: true,
    sellerNote: 'reconnect',
    event: 'PARTNER_REMOVED',
  },
  '1': {
    key: 'case1',
    variant: 'error',
    hasBody: true,
    primary: CTA.reconnect,
    secondary: CTA.stepByStep,
    inlineSupport: true,
    sellerNote: 'reconnect',
    event: 'PARTNER_REMOVED',
  },
  '2': {
    key: 'case2',
    variant: 'error',
    hasBody: true,
    primary: CTA.connectNewNumber,
    secondary: CTA.stepByStep,
    inlineSupport: true,
    sellerNote: 'reconnect',
    event: 'PARTNER_REMOVED',
  },
  '3': {
    key: 'case3',
    variant: 'error',
    hasBody: true,
    primary: CTA.reconnect,
    secondary: CTA.howToChange,
    sellerNote: 'reconnect',
    event: 'PARTNER_REMOVED',
  },
  '4': {
    key: 'case4',
    variant: 'error',
    hasBody: true,
    primary: CTA.scheduleSession,
    secondary: CTA.cloudApi,
    sellerNote: 'reconnect',
    event: 'PARTNER_REMOVED',
  },
  '5': {
    key: 'case4',
    variant: 'error',
    hasBody: true,
    primary: CTA.scheduleSession,
    secondary: CTA.cloudApi,
    sellerNote: 'reconnect',
    event: 'PARTNER_REMOVED',
  },
  '6': {
    key: 'case6',
    variant: 'error',
    hasBody: true,
    primary: CTA.connect,
    sellerNote: 'reconnect',
    event: 'ACCOUNT_DISCONNECTED',
  },
  '7': {
    key: 'case7',
    variant: 'error',
    hasBody: true,
    primary: CTA.support,
    sellerNote: 'support',
    event: 'ACCOUNT_DISCONNECTED',
  },
  '8a': {
    key: 'case8a',
    variant: 'info',
    hasBody: true,
    primary: null,
    sellerNote: 'reconnecting',
    event: 'ACCOUNT_OFFBOARDED',
  },
  '8b': {
    key: 'case8b',
    variant: 'warning',
    hasBody: true,
    primary: CTA.support,
    sellerNote: 'support',
    event: 'ACCOUNT_OFFBOARDED',
  },
  // Ajuste 3 (19-oct): a day out. Title pending Producto's final copy; until
  // then it keeps 8b's description and support button.
  '8d': {
    key: 'case8d',
    variant: 'warning',
    hasBody: true,
    primary: CTA.support,
    sellerNote: 'support',
    event: 'ACCOUNT_OFFBOARDED · 24h',
  },
  '8c_same': {
    key: 'case8cSame',
    variant: 'success',
    hasBody: false,
    primary: null,
    sellerNote: null,
    event: 'ACCOUNT_RECONNECTED',
  },
  '8c_other': {
    key: 'case8cOther',
    variant: 'warning',
    hasBody: true,
    primary: CTA.createTemplate,
    sellerNote: 'otherAccount',
    event: 'ACCOUNT_RECONNECTED',
  },
  '9': {
    key: 'case9',
    variant: 'error',
    hasBody: true,
    primary: CTA.support,
    sellerNote: 'support',
    event: 'PARTNER_REMOVED · CLOUD_API',
  },
  '10': {
    key: 'case10',
    variant: 'error',
    hasBody: true,
    primary: CTA.support,
    sellerNote: 'support',
    event: 'PARTNER_APP_UNINSTALLED',
  },
};

/** The roles that see the full notice and act on it (spec rule 1). */
function isActor(role: eUserRole): boolean {
  return role === eUserRole.OWNER || role === eUserRole.ADMIN;
}

function viewerRole(role: eUserRole): string {
  if (role === eUserRole.OWNER) return 'owner';
  if (role === eUserRole.ADMIN) return 'admin';
  return 'seller';
}

/** "evento · metaReason · initiatedBy", what support reads first. */
function supportReason(
  spec: tCaseSpec,
  disconnection: tWapiDisconnection | null,
): string {
  return [
    spec.event,
    disconnection?.metaReason,
    disconnection?.metaInitiatedBy,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * INT-1653 — the copy of a WhatsApp API disconnection notice for one viewer.
 *
 * The owner and the admin get the full notice: description, buttons and the
 * support link. Any other role cannot reconnect, so it gets the title adapted
 * to "tu cuenta" plus a note saying who can, and no buttons; the one
 * exception is "otra cuenta", where a seller can create templates too.
 *
 * `null` reads as case 0, the default notice: a DISCONNECTED row whose detail
 * is missing is still a disconnection.
 */
export function wapiDisconnectionCopy(options: {
  disconnection: tWapiDisconnection | null;
  role: eUserRole;
}): tAlertCopy {
  const { disconnection, role } = options;
  const disconnectionCase = disconnection?.case ?? '0';
  const spec = CASES[disconnectionCase];
  const prefix = `${NS}.${spec.key}`;
  const values = { reason: supportReason(spec, disconnection) };
  // Spec rule 6: error notices stay until the reconnection; only the two 8c
  // notices can be closed.
  const closable = CLOSABLE_WAPI_CASES.includes(disconnectionCase);
  const analytics = {
    meta_reason: disconnection?.metaReason ?? null,
    meta_initiated_by: disconnection?.metaInitiatedBy ?? null,
    disconnection_case: disconnectionCase,
    viewer_role: viewerRole(role),
  };

  if (isActor(role)) {
    return {
      variant: spec.variant,
      titleKey: `${prefix}.title`,
      bodyKey: spec.hasBody ? `${prefix}.body` : undefined,
      cta: spec.primary,
      secondaryCta: spec.secondary ?? null,
      inlineSupport: spec.inlineSupport
        ? {
            leadKey: `${NS}.supportLead`,
            labelKey: `${NS}.cta.support`,
            cta: CTA.support,
          }
        : undefined,
      values,
      analytics,
      closable,
    };
  }

  return {
    variant: spec.variant,
    titleKey: `${prefix}.sellerTitle`,
    // The seller's only button: they can create templates (spec, "otra
    // cuenta"). Every other case leaves them with the note.
    cta: disconnectionCase === '8c_other' ? CTA.createTemplate : null,
    note: spec.sellerNote
      ? {
          key: `${NS}.sellerNote.${spec.sellerNote}`,
          locked: spec.sellerNote !== 'otherAccount',
        }
      : undefined,
    values,
    analytics,
    closable,
  };
}
